import React from 'react'
import {
  Modal,
  Card,
  Row,
  Col,
  Badge,
  ProgressBar,
  Spinner,
  Alert,
  Table,
  Button,
} from 'react-bootstrap'
import { IndividualBehavioralProfile } from 'interfaces/behavioral-profile'

interface IndividualBehavioralProfileModalProps {
  show: boolean
  onHide: () => void
  profile: IndividualBehavioralProfile | null
  loading: boolean
  error: Error | null
  systemUser: string
  recipient: string
}

const IndividualBehavioralProfileModal: React.FC<
  IndividualBehavioralProfileModalProps
> = ({ show, onHide, profile, loading, error, systemUser, recipient }) => {
  // Helper function to get risk level color
  const getRiskLevelColor = (score: number) => {
    if (score >= 80) return 'danger'
    if (score >= 60) return 'warning'
    if (score >= 40) return 'info'
    return 'success'
  }

  // Helper function to get escalation level color
  const getEscalationLevelColor = (level: number) => {
    if (level >= 3) return 'danger'
    if (level >= 2) return 'warning'
    if (level >= 1) return 'info'
    return 'success'
  }

  const formatPhoneNumber = (phone: string) => {
    return phone.replace(/^(\+1)(\d{3})(\d{3})(\d{4})$/, '$1 ($2) $3-$4')
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      backdrop
      className="behavioral-profile-modal"
    >
      <Modal.Header
        className="bg-dark text-white border-secondary d-flex justify-content-between align-items-center"
        style={{
          background: 'linear-gradient(135deg, #2d3748 0%, #1a202c 100%)',
          borderBottom: '2px solid #4a5568',
        }}
      >
        <Modal.Title>
          <div>
            <div className="fw-bold">Conversation Analysis</div>
            <small className="text-muted">
              {formatPhoneNumber(systemUser)} → {formatPhoneNumber(recipient)}
            </small>
          </div>
        </Modal.Title>
        <Button
          variant="link"
          className="p-0 border-0"
          onClick={onHide}
          style={{
            color: '#dc3545',
            fontSize: '24px',
            lineHeight: '1',
            textDecoration: 'none',
          }}
        >
          ×
        </Button>
      </Modal.Header>

      <Modal.Body className="bg-dark text-white p-4">
        {loading ? (
          <div className="text-center p-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading profile...</span>
            </Spinner>
            <div className="mt-2">Loading behavioral profile...</div>
          </div>
        ) : error ? (
          <Alert variant="warning">
            <Alert.Heading>Profile Not Available</Alert.Heading>
            <p>Unable to load behavioral profile data for this conversation.</p>
            <small className="text-muted">Error: {error.message}</small>
          </Alert>
        ) : !profile ? (
          <Alert variant="info">
            <Alert.Heading>No Profile Data</Alert.Heading>
            <p>This conversation doesn't have behavioral profile data yet.</p>
          </Alert>
        ) : (
          <>
            {/* Can Send Decision */}
            <Row className="mb-4">
              <Col>
                <Card
                  className="border border-secondary text-white shadow-sm"
                  style={{
                    borderRadius: '12px',
                    background: profile.canSend
                      ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                      : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  }}
                >
                  <Card.Body className="text-center p-4">
                    <div className="d-flex justify-content-center align-items-center gap-3">
                      <span style={{ fontSize: '48px' }}>
                        {profile.canSend ? '✅' : '🚫'}
                      </span>
                      <div>
                        <div className="display-6 fw-bold">
                          {profile.canSend ? 'ALLOWED' : 'BLOCKED'}
                        </div>
                        <div className="text-light">Current Decision</div>
                      </div>
                    </div>
                    <div className="mt-3">
                      <small className="text-light">
                        {profile.lastCanSendDecision.reason}
                      </small>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Key Metrics */}
            <Row className="mb-4">
              <Col md={3} className="mb-3">
                <Card
                  className="h-100 border-2 text-white shadow-lg"
                  style={{
                    borderRadius: '12px',
                    background:
                      'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    border: '2px solid rgba(59, 130, 246, 0.3)',
                  }}
                >
                  <Card.Body className="text-center p-4">
                    <div className="display-6 fw-bold mb-2">
                      {profile.scoring.conversationScore != null
                        ? profile.scoring.conversationScore.toFixed(1)
                        : '-'}
                    </div>
                    <div className="text-light fw-semibold">
                      Conversation Score
                    </div>
                    <ProgressBar
                      now={profile.scoring.conversationScore}
                      className="mt-2"
                      style={{
                        height: '6px',
                        borderRadius: '3px',
                        backgroundColor: 'rgba(255,255,255,0.2)',
                      }}
                    />
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3} className="mb-3">
                <Card
                  className="h-100 border-2 text-white shadow-lg"
                  style={{
                    borderRadius: '12px',
                    background:
                      'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    border: '2px solid rgba(245, 158, 11, 0.3)',
                  }}
                >
                  <Card.Body className="text-center p-4">
                    <div className="display-6 fw-bold mb-2">
                      {profile.deviations.riskScore != null
                        ? profile.deviations.riskScore.toFixed(1)
                        : '-'}
                    </div>
                    <div className="text-light fw-semibold">Risk Score</div>
                    <ProgressBar
                      now={profile.deviations.riskScore}
                      className="mt-2"
                      style={{
                        height: '6px',
                        borderRadius: '3px',
                        backgroundColor: 'rgba(255,255,255,0.2)',
                      }}
                    />
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3} className="mb-3">
                <Card
                  className="h-100 border-2 text-white shadow-lg"
                  style={{
                    borderRadius: '12px',
                    background:
                      'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    border: '2px solid rgba(139, 92, 246, 0.3)',
                  }}
                >
                  <Card.Body className="text-center p-4">
                    <div className="display-6 fw-bold mb-2">
                      {profile.current.recentSentiment != null
                        ? profile.current.recentSentiment.toFixed(1)
                        : '-'}
                    </div>
                    <div className="text-light fw-semibold">
                      Recent Sentiment
                    </div>
                    <ProgressBar
                      now={profile.current.recentSentiment}
                      className="mt-2"
                      style={{
                        height: '6px',
                        borderRadius: '3px',
                        backgroundColor: 'rgba(255,255,255,0.2)',
                      }}
                    />
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3} className="mb-3">
                <Card
                  className="h-100 border-2 text-white shadow-lg"
                  style={{
                    borderRadius: '12px',
                    background:
                      profile.current.escalationLevel >= 3
                        ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                        : profile.current.escalationLevel >= 2
                        ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                        : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    border: `2px solid rgba(${
                      profile.current.escalationLevel >= 3
                        ? '239, 68, 68'
                        : profile.current.escalationLevel >= 2
                        ? '245, 158, 11'
                        : '16, 185, 129'
                    }, 0.3)`,
                  }}
                >
                  <Card.Body className="text-center p-4">
                    <div className="display-6 fw-bold mb-2">
                      {profile.current.escalationLevel != null
                        ? profile.current.escalationLevel
                        : '-'}
                    </div>
                    <div className="text-light fw-semibold">
                      Escalation Level
                    </div>
                    <small className="text-light">
                      {profile.deviations.alertLevel}
                    </small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Patterns and Quality */}
            <Row className="mb-4">
              <Col md={6} className="mb-3">
                <Card className="h-100 border border-light bg-dark text-white shadow-lg">
                  <Card.Header className="bg-dark border-bottom border-light">
                    <h6 className="text-white mb-0 fw-bold">
                      Behavioral Patterns
                    </h6>
                  </Card.Header>
                  <Card.Body className="p-4">
                    <div
                      className="d-flex justify-content-between mb-3 p-3 rounded"
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        border: '1px solid rgba(255,255,255,0.2)',
                      }}
                    >
                      <span className="text-white fw-semibold">Opt-outs</span>
                      <span className="fw-bold text-danger fs-4">
                        {profile.patterns.hasOptedOut ? '1' : '-'}
                      </span>
                    </div>
                    <div
                      className="d-flex justify-content-between mb-3 p-3 rounded"
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        border: '1px solid rgba(255,255,255,0.2)',
                      }}
                    >
                      <span className="text-white fw-semibold">
                        Positive Interactions
                      </span>
                      <span className="fw-bold text-success fs-4">
                        {profile.patterns.positiveInteractions || '-'}
                      </span>
                    </div>
                    <div
                      className="d-flex justify-content-between p-3 rounded"
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        border: '1px solid rgba(255,255,255,0.2)',
                      }}
                    >
                      <span className="text-white fw-semibold">
                        Negative Interactions
                      </span>
                      <span className="fw-bold text-danger fs-4">
                        {profile.patterns.negativeInteractions || '-'}
                      </span>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6} className="mb-3">
                <Card className="h-100 border border-light bg-dark text-white shadow-lg">
                  <Card.Header className="bg-dark border-bottom border-light">
                    <h6 className="text-white mb-0 fw-bold">
                      Message Statistics
                    </h6>
                  </Card.Header>
                  <Card.Body className="p-4">
                    <div
                      className="d-flex justify-content-between mb-3 p-3 rounded"
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        border: '1px solid rgba(255,255,255,0.2)',
                      }}
                    >
                      <span className="text-white fw-semibold">
                        Inbound Messages
                      </span>
                      <span className="fw-bold text-info fs-4">
                        {profile.patterns.totalInboundMessages || '-'}
                      </span>
                    </div>
                    <div
                      className="d-flex justify-content-between mb-3 p-3 rounded"
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        border: '1px solid rgba(255,255,255,0.2)',
                      }}
                    >
                      <span className="text-white fw-semibold">
                        Outbound Messages
                      </span>
                      <span className="fw-bold text-info fs-4">
                        {profile.patterns.totalOutboundMessages || '-'}
                      </span>
                    </div>
                    <div
                      className="d-flex justify-content-between mb-3 p-3 rounded"
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        border: '1px solid rgba(255,255,255,0.2)',
                      }}
                    >
                      <span className="text-white fw-semibold">
                        Avg Message Quality
                      </span>
                      <span className="fw-bold text-success fs-4">
                        {profile.scoring.qualityMetrics.avgMessageQuality !=
                        null
                          ? profile.scoring.qualityMetrics.avgMessageQuality.toFixed(
                              1
                            )
                          : '-'}
                      </span>
                    </div>
                    <div
                      className="d-flex justify-content-between p-3 rounded"
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        border: '1px solid rgba(255,255,255,0.2)',
                      }}
                    >
                      <span className="text-white fw-semibold">
                        Avg Spam Likelihood
                      </span>
                      <span className="fw-bold text-warning fs-4">
                        {profile.scoring.qualityMetrics.avgSpamLikelihood !=
                        null
                          ? profile.scoring.qualityMetrics.avgSpamLikelihood.toFixed(
                              1
                            )
                          : '-'}
                      </span>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* First Message Quality (if available) */}
            {profile.firstMessageQuality && (
              <Row className="mb-4">
                <Col>
                  <Card className="border border-secondary bg-secondary text-white shadow-sm">
                    <Card.Header className="bg-secondary border-bottom border-secondary">
                      <h6 className="text-light mb-0 fw-semibold">
                        🎯 First Message Quality Analysis
                      </h6>
                    </Card.Header>
                    <Card.Body className="p-4">
                      <Row>
                        <Col md={3} className="mb-3">
                          <div className="text-center">
                            <div className="display-6 fw-bold text-primary mb-2">
                              {profile.firstMessageQuality
                                .personalizationScore != null
                                ? profile.firstMessageQuality
                                    .personalizationScore
                                : '-'}
                            </div>
                            <div className="text-light">Personalization</div>
                          </div>
                        </Col>
                        <Col md={3} className="mb-3">
                          <div className="text-center">
                            <div className="display-6 fw-bold text-info mb-2">
                              {profile.firstMessageQuality
                                .coldOutreachQuality != null
                                ? profile.firstMessageQuality
                                    .coldOutreachQuality
                                : '-'}
                            </div>
                            <div className="text-light">Outreach Quality</div>
                          </div>
                        </Col>
                        <Col md={3} className="mb-3">
                          <div className="text-center">
                            <Badge
                              variant={
                                profile.firstMessageQuality.isPersonalized
                                  ? 'success'
                                  : 'warning'
                              }
                              className="p-2"
                            >
                              {profile.firstMessageQuality.isPersonalized
                                ? 'PERSONALIZED'
                                : 'TEMPLATE'}
                            </Badge>
                            <div className="text-light mt-2">Message Type</div>
                          </div>
                        </Col>
                        <Col md={3} className="mb-3">
                          <div className="text-center">
                            <Badge
                              variant={
                                profile.firstMessageQuality.qualityTier ===
                                'HIGH'
                                  ? 'success'
                                  : profile.firstMessageQuality.qualityTier ===
                                    'MEDIUM'
                                  ? 'warning'
                                  : 'danger'
                              }
                              className="p-2"
                            >
                              {profile.firstMessageQuality.qualityTier}
                            </Badge>
                            <div className="text-light mt-2">Quality Tier</div>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Metadata */}
            <Row>
              <Col>
                <small className="text-muted">
                  Data source: {profile.metadata.source} |{' '}
                  {profile.metadata.lastUpdated && (
                    <>
                      Last updated:{' '}
                      {new Date(profile.metadata.lastUpdated).toLocaleString()}{' '}
                      |{' '}
                    </>
                  )}
                  Last message:{' '}
                  {new Date(profile.current.lastMessageTime).toLocaleString()}
                </small>
              </Col>
            </Row>
          </>
        )}
      </Modal.Body>
    </Modal>
  )
}

export default IndividualBehavioralProfileModal
